// Diálogo de confirmación para acciones DESTRUCTIVAS (borrar, sacar, vaciar).
//
// Vivía dentro de `pages/lanzamientos/LanzamientoDetalle.tsx` como función local —
// ADR-2026-09-08-la-confirmacion-de-borrar-es-un-modal-compartido. Se extrajo cuando el
// historial de chats necesitó exactamente el mismo diálogo: copiarlo era la segunda
// copia de un `alertdialog` con portal, Escape, estado "trabajando" y botón rojo, o sea
// el punto exacto en el que las dos empiezan a divergir.
//
// Sirve para reemplazar a `window.confirm`: el nativo no se puede estilar, no dice de
// qué app viene y en mobile aparece pegado al chrome del navegador.
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Trash2, Loader2 } from 'lucide-react';

export interface ModalConfirmarProps {
  abierto: boolean;
  titulo: string;
  detalle: string;
  /** Rótulo del botón rojo. Que diga la ACCIÓN ("Borrar los 12 chats"), no "Aceptar". */
  confirmar: string;
  /** `true` mientras la acción corre: bloquea los dos botones y muestra el spinner. */
  trabajando?: boolean;
  onCancelar: () => void;
  onConfirmar: () => void | Promise<void>;
}

export function ModalConfirmar({
  abierto,
  titulo,
  detalle,
  confirmar,
  trabajando,
  onCancelar,
  onConfirmar,
}: ModalConfirmarProps) {
  // Escape cierra, como cualquier diálogo de la app.
  useEffect(() => {
    if (!abierto) return;
    const alaTecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !trabajando) onCancelar();
    };
    document.addEventListener('keydown', alaTecla);
    return () => document.removeEventListener('keydown', alaTecla);
  }, [abierto, trabajando, onCancelar]);

  if (!abierto) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[150] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      onClick={() => !trabajando && onCancelar()}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={titulo}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-3xl bg-white p-6 sm:rounded-3xl"
      >
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-red-50 text-red-600">
            <Trash2 className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-brand-black">{titulo}</h2>
            <p className="mt-1 text-sm leading-relaxed text-neutral-500">{detalle}</p>
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            disabled={trabajando}
            onClick={onCancelar}
            className="flex-1 rounded-xl border border-neutral-200 py-2.5 px-5 text-sm text-brand-black disabled:opacity-40"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={trabajando}
            onClick={() => void onConfirmar()}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-600 py-2.5 px-5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40"
          >
            {trabajando && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmar}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ModalConfirmar;
